import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Normalize: accept array of strings OR array of {url, caption}
function normalize(items) {
  if (!items) return [];
  return items.map((it) => (typeof it === "string" ? { url: it, caption: "" } : it));
}

export default function Lightbox({ images, open, index: initialIndex = 0, onClose }) {
  const slides = normalize(images);
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex, open]);

  const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, next, prev]);

  if (!open || slides.length === 0) return null;
  const current = slides[index];

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center fade-up"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-6 right-6 text-white/80 hover:text-white p-2 z-10"
        aria-label="Close"
      >
        <X size={28} />
      </button>

      <div className="absolute top-6 left-6 text-white/70 text-xs uppercase tracking-[0.18em] z-10">
        {index + 1} / {slides.length}
      </div>

      {slides.length > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-6 md:left-12 text-white/80 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            aria-label="Previous"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-6 md:right-12 text-white/80 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            aria-label="Next"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      <div className="flex flex-col items-center max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
        <img
          src={current.url}
          alt={current.caption || `Gallery image ${index + 1}`}
          className="max-w-[92vw] max-h-[78vh] object-contain shadow-2xl"
        />
        {current.caption && (
          <p
            data-testid="lightbox-caption"
            className="mt-6 text-center text-[#FAF6F0]/90 font-display italic text-lg md:text-xl px-6 max-w-3xl leading-relaxed"
          >
            {current.caption}
          </p>
        )}
      </div>
    </div>
  );
}
